import { createApi } from "@reduxjs/toolkit/query/react";

import { ICustomerUser } from "@/interfaces/customer-user.interface";
import { ICustomer } from "@/interfaces/customer.interface";
import { customerUser } from "@/interfaces/interfaces";
import { IProspect, IProspectUser } from "@/interfaces/prospect.interface";
import { TSpecialRate } from "@/interfaces/special-rate.interface";
import { BaseWithAuth, getToken, getWorkspace } from "@/utils/helpers";

export const clientApi = createApi({
  reducerPath: "clientApi",
  baseQuery: BaseWithAuth(),
  tagTypes: [
    "customer",
    "customer-user",
    "prospect",
    "prospectUser",
    "specialRate",
    "single-prospect",
  ],
  endpoints: (builder) => ({
    // getCustomerList by WS
    getCustomerByAdmin: builder.query<
      GeneralResponse<ICustomer[]> | GeneralResponse<customerUser[]>,
      { user: string; status: string; filter?: string }
    >({
      query: ({ user, status, filter }) =>
        `superadmin/customer/${user ? user : ""}${status ? `?status=${status}` : ""}${filter ? filter : ""}`,
      providesTags: ["customer", "customer-user"],
    }),

    // getCustomerList by WS
    getAllCustomerByWS: builder.query<
      GeneralResponse<ICustomer[]> | GeneralResponse<customerUser[]>,
      { user?: string; status: string; filter?: string; customerId?: string }
    >({
      query: ({ user, status, filter, customerId }) =>
        `workspace/${getWorkspace()}/customer/${user ? user : ""}${status ? `?status=${status}` : ""}${filter ? filter : ""}${customerId ? `&customerId=${customerId}` : ""}`,
      providesTags: ["customer", "customer-user"],
    }),

    getCustomer: builder.query<
      GeneralResponse<ICustomer> | GeneralResponse<ICustomer>[],
      { id?: string; filter?: string }
    >({
      query: ({ id, filter }) =>
        `workspace/${getWorkspace()}/customer/${id ? id : ""}${filter ? filter : ""}`,
      providesTags: ["customer"],
    }),

    toggleCustomerStatus: builder.mutation<
      unknown,
      { userType: string; userId: string; status: string }
    >({
      query: ({ userType, userId, status }) => ({
        url: `/workspace/${getWorkspace()}/${userType}/${userId}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["customer"],
    }),

    getCustomerUser: builder.query<
      GeneralResponse<ICustomerUser[]>,
      { customerId?: string; filter?: string; status?: string }
    >({
      query: ({ customerId, filter, status }) => ({
        url: `workspace/${getWorkspace()}/customer/user?${customerId ? `customerId=${customerId}` : ""}${filter ? filter : ""}${status ? `&status=${status}` : ""}`,
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }),
      providesTags: ["customer-user"],
      // transformResponse: (response: APIResponse<ICustomerUser[]>) =>
      //   response?.data,
    }),

    // add customer
    addCustomer: builder.mutation<object, { data: object }>({
      query: ({ data }) => ({
        url: `workspace/${getWorkspace()}/customer`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["customer", "customer-user"],
    }),
    alterCustomer: builder.mutation<object, unknown>({
      query: ({ data, userId }) => ({
        url: `/workspace/${getWorkspace()}/customer/${userId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["customer"],
    }),

    // add customer users
    addCustomerUser: builder.mutation<object, { data: object }>({
      query: ({ data }) => ({
        url: `workspace/${getWorkspace()}/customer/user`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["customer-user", "customer"],
    }),

    // handle Portal Access
    handlePortal: builder.mutation<
      object,
      { method?: string; custUserId: string; data: boolean }
    >({
      query: ({ custUserId, data }) => ({
        url: `workspace/${getWorkspace()}/customer/user/${custUserId}/portal`,
        method: "PATCH",
        body: { portalAccess: data },
      }),
      invalidatesTags: ["customer-user", "customer"],
    }),

    getProspects: builder.query<
      GeneralResponse<IProspect[]> | GeneralSingleResponse<IProspect>,
      string
    >({
      query: (id) => ({
        url: `workspace/${getWorkspace()}/prospect/${id ? id : ""}`,
      }),
      providesTags: ["prospect"],
    }),

    getSingleProspect: builder.query<GeneralSingleResponse<IProspect>, string>({
      query: (id) => ({
        url: `workspace/${getWorkspace()}/prospect/${id ? id : ""}`,
      }),
      providesTags: ["single-prospect"],
    }),

    alterProspect: builder.mutation<
      object,
      { data?: object; id?: string; method: string }
    >({
      query: ({ data, id, method }) => ({
        url: `workspace/${getWorkspace()}/prospect/${id ? id : ""}`,
        method: method.trim().toUpperCase(),
        body: data,
      }),
      invalidatesTags: ["prospect", "prospectUser"],
    }),

    // getProspectUser
    getProspectUser: builder.query<
      GeneralSingleResponse<IProspectUser> | GeneralResponse<IProspectUser[]>,
      { id?: string; filter?: string; prospectId?: string }
    >({
      query: ({ id, filter, prospectId }) =>
        `workspace/${getWorkspace()}/prospect/user/${id ? id : ""}${prospectId ? `?prospectId=${prospectId}` : ""}${filter ? filter : ""}`,
      providesTags: ["prospectUser"],
    }),

    alterProspectUser: builder.mutation<
      object,
      { data?: object; id?: string; method: string }
    >({
      query: ({ data, id, method }) => ({
        url: `workspace/${getWorkspace()}/prospect/user/${id ? id : ""}`,
        method: method.trim().toUpperCase(),
        body: data,
      }),
      invalidatesTags: ["prospectUser"],
    }),

    alterTransferProspect: builder.mutation<
      object,
      { data?: object; id?: string; method: string }
    >({
      query: ({ data, id, method }) => ({
        url: `workspace/${getWorkspace()}/prospect/${id ? id : ""}/transfer`,
        method: method.trim().toUpperCase(),
        body: data,
      }),
      invalidatesTags: ["prospect", "customer", "prospectUser"],
    }),

    syncCustomerToXero: builder.mutation<object, { id: string }>({
      query: ({ id }) => ({
        url: `workspace/${getWorkspace()}/customer/${id}/xero`,
        method: "PATCH",
      }),
      invalidatesTags: ["customer"],
    }),
    specialRate: builder.mutation<object, { id: string; data: object }>({
      query: ({ data, id }) => ({
        url: `workspace/${getWorkspace()}/customer/${id}/rate`,
        method: "PATCH",
        body: data,
      }),
    }),
    getCustomerSpecialRate: builder.query<TSpecialRate, { id: string }>({
      query: ({ id }) => `workspace/${getWorkspace()}/customer/${id}/rate`,
      providesTags: ["specialRate"],
      transformResponse: (response: APIResponse<TSpecialRate>) =>
        response?.data,
    }),
  }),
});

export const {
  useGetCustomerByAdminQuery,
  useHandlePortalMutation,
  useGetCustomerQuery,
  useToggleCustomerStatusMutation,
  useGetAllCustomerByWSQuery,
  useGetCustomerUserQuery,
  useAlterCustomerMutation,
  useAddCustomerMutation,
  useAddCustomerUserMutation,
  useGetProspectsQuery,
  useGetSingleProspectQuery,
  useAlterProspectMutation,
  useGetProspectUserQuery,
  useAlterProspectUserMutation,
  useAlterTransferProspectMutation,
  useSyncCustomerToXeroMutation,
  useSpecialRateMutation,
  useGetCustomerSpecialRateQuery,
} = clientApi;
